import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, Grid, TextField } from '@mui/material';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { useDispatch } from 'react-redux';
import { updateCompany } from '../../../redux/company/actions';

interface ICompanyContactEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const CompanyContactEdit: React.FC<ICompanyContactEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        email: company?.email || '',
        phone_number: company?.phone_number || '',
    });
    const [initialData, setInitialData] = useState(formData);

    useEffect(() => {
        if (company) {
            const newData = {
                email: company.email || '',
                phone_number: company.phone_number || '',
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleFieldChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        if (company?._id) {
            const payload = {
                id: company._id,
                body: {
                    email: formData.email,
                    phone_number: formData.phone_number,
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    // Register the save function with the parent container
    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    return (
        <Card sx={{ bgcolor: theme[themeStyle].secondaryBg, border: `1px solid ${theme[themeStyle].tag_white_light}` }}>
            <CardHeader
                title="Contact Information"
                sx={{ color: theme[themeStyle].tag_white }}
                titleTypographyProps={{
                    sx: { fontFamily: FONT_ENUM.MAIN_BOLD },
                }}
            />
            <CardContent>
                <Grid container spacing={3}>
                    {/* Email */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={e => handleFieldChange('email', e.target.value)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    {/* Phone Number */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.phone_number}
                            onChange={e => handleFieldChange('phone_number', e.target.value)}
                            disabled={!isEditing}
                            sx={{
                                '& .MuiInputBase-input': {
                                    color: theme[themeStyle].tag_white,
                                },
                                '& .MuiInputLabel-root': {
                                    color: theme[themeStyle].lightWhite,
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: theme[themeStyle].tag_white,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: theme[themeStyle].highlight,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export { CompanyContactEdit };
